import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Home, ShoppingBag, PackageCheck, LogOut, Loader2, ListChecks } from "lucide-react";

export default function PortalLayout() {
    const { client, loading, logout } = usePortalAuth();
    const nav = useNavigate();

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-500">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading…
        </div>;
    }
    if (!client) return <Navigate to="/portal" replace />;

    const linkCls = ({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
            isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
        }`;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/20 to-amber-50/20" data-testid="portal-shell">
            {/* Top bar */}
            <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
                    <Link to="/portal/dashboard" className="flex items-center gap-2 shrink-0">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 grid place-items-center text-white font-black text-sm">H</div>
                        <div className="leading-tight">
                            <div className="font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>HDB Client Portal</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Har Din Bachat</div>
                        </div>
                    </Link>
                    <nav className="ml-4 hidden md:flex gap-1">
                        <NavLink to="/portal/dashboard" className={linkCls} data-testid="portal-nav-dashboard">
                            <Home size={14} /> Dashboard
                        </NavLink>
                        <NavLink to="/portal/order" className={linkCls} data-testid="portal-nav-order">
                            <ShoppingBag size={14} /> Place Order
                        </NavLink>
                        <NavLink to="/portal/orders" className={linkCls} data-testid="portal-nav-orders">
                            <PackageCheck size={14} /> My Orders
                        </NavLink>
                        <NavLink to="/portal/essentials-planner" className={linkCls} data-testid="portal-nav-planner">
                            <ListChecks size={14} /> Plan Essentials
                        </NavLink>
                    </nav>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="text-right hidden sm:block">
                            <div className="text-xs text-slate-500 uppercase tracking-wider">Signed in as</div>
                            <div className="text-sm font-bold text-slate-900" data-testid="portal-client-name">{client.name}</div>
                        </div>
                        <button onClick={async () => { await logout(); nav("/portal", { replace: true }); }}
                                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Sign out"
                                data-testid="portal-logout-btn">
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>
                {/* Mobile nav */}
                <div className="md:hidden border-t border-slate-100 px-2 py-1 flex gap-1 overflow-x-auto">
                    <NavLink to="/portal/dashboard" className={linkCls}><Home size={14}/> Dashboard</NavLink>
                    <NavLink to="/portal/order" className={linkCls}><ShoppingBag size={14}/> Order</NavLink>
                    <NavLink to="/portal/orders" className={linkCls}><PackageCheck size={14}/> Orders</NavLink>
                    <NavLink to="/portal/essentials-planner" className={linkCls}><ListChecks size={14}/> Plan</NavLink>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
                <Outlet />
            </main>
        </div>
    );
}
